import { useMemo, useCallback } from 'react';
import { useBookingContext } from 'src/widgets/BookingContext';
import { useLocale } from '@repo/i18n';
import { useConfigurations } from '@repo/widget-utils/widgetsConfiguration';
import {
    buildPackageContext,
    validatePackageQuantities,
    updatePackageQuantity,
    toPackageInputItems,
} from '@repo/widget-utils/booking/domains/package/packageQuantities';
import { applyPersonsMaxLimit } from '@repo/widget-utils/booking/domains/shared/capacity-calculators';
import { toDefaultInputItems } from '@repo/widget-utils/booking/domains/shared/transformers';
import {
    mergeWithDefaultItems,
    hasUnavailableItems,
} from '@repo/widget-utils/booking/domains/shared/mergeInputItems';
import type {
    QuantityInputItem,
    QuantityError,
} from '@repo/widget-utils/booking/domains/shared/types';
import { setQuantities } from 'src/state/booking/bookingReducer';

export type UsePackageQuantitiesResult = {
    /** UI-ready input items for the MultipleNumberInput component */
    inputItems: QuantityInputItem[];

    /** Validation errors (empty array if no errors) */
    errors: QuantityError[];

    /** Handler for quantity changes */
    onChangeQuantity: (ticketCategoryId: string, newQuantity: number) => void;

    /** Convenience flag - true if any validation errors exist */
    hasErrors: boolean;

    /** Message to show when some tickets are unavailable for selected date/time */
    disabledMessage?: string;
};

export function usePackageQuantities(
    quantitiesOverride?: Parameters<typeof buildPackageContext>[0],
): UsePackageQuantitiesResult {
    const {
        quantities: contextQuantities,
        defaultTicketOptions,
        selectedProducts,
        bookingDispatch,
        attemptedBooking,
    } = useBookingContext();
    const { t } = useLocale();
    const config = useConfigurations();

    // Use override if provided, otherwise fall back to context
    const quantities = quantitiesOverride ?? contextQuantities;

    const context = useMemo(
        () => buildPackageContext(quantities, selectedProducts || []),
        [quantities, selectedProducts],
    );

    const errors = useMemo(
        () => validatePackageQuantities(quantities, context, attemptedBooking),
        [quantities, context, attemptedBooking],
    );

    const availableInputItems = useMemo(() => {
        const items = toPackageInputItems(quantities, context, t, errors);

        if (config.personsMax && config.personsMax > 0) {
            return applyPersonsMaxLimit(items, config.personsMax);
        }

        return items;
    }, [quantities, context, errors, t, config.personsMax]);

    const defaultInputItems = useMemo(
        () => toDefaultInputItems(defaultTicketOptions, t),
        [defaultTicketOptions, t],
    );

    const inputItems = useMemo(() => {
        if (defaultInputItems.length === 0) {
            return availableInputItems;
        }
        return mergeWithDefaultItems(availableInputItems, defaultInputItems);
    }, [availableInputItems, defaultInputItems]);

    const disabledMessage = useMemo(() => {
        if (defaultInputItems.length === 0) return undefined;
        return hasUnavailableItems(inputItems)
            ? t.some_tickets_are_unavailable_the_selected_day
            : undefined;
    }, [inputItems, defaultInputItems.length, t]);

    const onChangeQuantity = useCallback(
        (ticketCategoryId: string, newQuantity: number) => {
            const updatedQuantities = updatePackageQuantity(
                quantities,
                ticketCategoryId,
                newQuantity,
            );
            bookingDispatch(setQuantities(updatedQuantities));
        },
        [quantities, bookingDispatch],
    );

    const hasErrors = errors.length > 0;

    return {
        inputItems,
        errors,
        onChangeQuantity,
        hasErrors,
        disabledMessage,
    };
}
